import type z from "zod";

import type { StructuralDecorator } from "../decorator.js";
import { defineRule } from "../rules.js";

/**
 * Requires the comment of every item drawing on a response set once it is
 * answered with one of some values: a rule for a kind of item, declared once
 * rather than for each key. A `commentRequiredWhen` after it overrides it for
 * one item, as any later decorator overrides an earlier one.
 *
 * The set is matched by its id, never by the values it offers, since two
 * scales may offer the same values and mean different things by them. A set
 * declared without an id matches no item.
 */
export const commentRequiredForAnswers = <ResponseSet extends z.ZodType>(
  responseSet: ResponseSet,
  values: readonly z.output<ResponseSet>[],
): StructuralDecorator => {
  const id = responseSet.meta()?.id;
  const named = `${String(id)} answered ${values.map((value) => JSON.stringify(value)).join(" or ")}`;
  // Widened because a rule sees every answer as `unknown`; only an item on
  // this set is given this rule.
  const required: readonly unknown[] = values;

  return {
    kind: "structural",
    item: (view) =>
      id !== undefined && view.responseSet === id
        ? {
            ...view,
            commentRequired: defineRule(named, ({ answers }) =>
              required.includes(answers[view.at.item]),
            ),
          }
        : view,
  };
};
